import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';

const EVT = 'drive:check-renames';
const TYPE_LABEL = { client: 'client folder', property: 'property folder', job: 'job folder', stage: 'stage folder', client_docs: 'client uploads folder', property_docs: 'property uploads folder', file: 'file' };

// Call after a client/property/layout change: { client_id } | { property_id } | { all: true }.
export const requestDriveRenameCheck = (detail) => window.dispatchEvent(new CustomEvent(EVT, { detail }));

export const pollDriveJob = async (api, jobId) => {
  for (let i = 0; i < 120 && jobId; i += 1) {
    const { data } = await api.get(`/drive/jobs/${jobId}`);
    if (data.status === 'done') return data;
    await new Promise((r) => setTimeout(r, 1500));
  }
  return null;
};

export const DriveRenamePrompt = () => {
  const { api } = useAuth();
  const [plan, setPlan] = useState(null);
  const [scope, setScope] = useState({});

  useEffect(() => {
    const onCheck = async (e) => {
      const d = e.detail || {};
      const qs = d.client_id ? `client_id=${d.client_id}` : d.property_id ? `property_id=${d.property_id}` : '';
      try {
        const { data } = await api.get(`/drive/rename-plan${qs ? `?${qs}` : ''}`);
        if (data.connected && data.total > 0) { setScope(d); setPlan(data); }
        else if (d.force) toast.success('Everything in Drive already matches the current names');
      } catch { /* Drive optional */ }
    };
    window.addEventListener(EVT, onCheck);
    return () => window.removeEventListener(EVT, onCheck);
  }, [api]);

  const apply = async () => {
    const p = plan; setPlan(null);
    try {
      const { data } = await api.post('/drive/rename-apply', { client_id: scope.client_id || '', property_id: scope.property_id || '' });
      toast.message(`Renaming ${data.total} item${data.total === 1 ? '' : 's'} in Google Drive…`);
      const job = await pollDriveJob(api, data.job_id);
      if (job?.errors?.length) toast.error(`${job.errors.length} rename(s) failed — see Drive Log`);
      else toast.success('Google Drive names updated');
    } catch (e) { toast.error(e?.response?.data?.detail || `Rename failed (${p?.total} items)`); }
  };

  const summary = plan ? Object.entries(plan.counts).map(([k, n]) => `${n} ${TYPE_LABEL[k] || k}${n === 1 ? '' : 's'}`).join(', ') : '';
  return (
    <AlertDialog open={!!plan} onOpenChange={(o) => !o && setPlan(null)}>
      <AlertDialogContent data-testid="drive-rename-dialog" className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Update names in Google Drive?</AlertDialogTitle>
          <AlertDialogDescription>
            This will rename {summary} so Drive matches the CRM{plan?.simulated ? ' (test mode)' : ''}. Anyone with these files open offline may see a conflict copy — large batches are best run after hours.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="max-h-48 overflow-y-auto text-[11px] font-mono bg-slate-50 rounded p-2 space-y-0.5" data-testid="drive-rename-list">
          {(plan?.items || []).slice(0, 30).map((it) => <div key={it.drive_id}><span className="text-slate-400">{it.current}</span> → {it.expected}</div>)}
          {plan?.items?.length > 30 && <div className="text-slate-400">…and {plan.items.length - 30} more</div>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="drive-rename-later-btn">Later</AlertDialogCancel>
          <AlertDialogAction data-testid="drive-rename-now-btn" onClick={apply} className="bg-[#0F4F1B] hover:bg-[#0a3c14]">Rename now</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
